import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, Typography, IconButton } from '@mui/material';
import { DeleteOutlined, ArrowBack } from '@mui/icons-material';
import { useEditorStore, useAssistantLoadingStore } from '@diana/core';
import { useFindActiveTask } from '../../../Support/useFindActiveTask';
import { FLOW_BUILDER2_PAGE_NS } from '../../../i18n';
import { useFlowBuilder2PageStore } from '../../../Store/useFlowBuilder2PageStore';
import { ConfigureDrawerHeader } from '../ConfigureDrawer.styles';
import { isEqual, uniq, uniqBy } from 'lodash';
import { useDeleteTask } from '../../../Support/useDeleteTask';
import { BuilderConfigureEditorTabEnum } from '../../../Store/FlowBuilder2PageStore.types';
import { useDeleteStep } from '../../../Support/useDeleteStep';
import { useDeleteAction } from '../../../Support/useDeleteAction';
import {
  IRightDrawerAction,
  RightDrawerActions,
} from '../../../Components/RightDrawer/RightDrawerActions';
import { RightDrawerHeaderSwitcher } from '../../../Components/RightDrawer/RightDrawerHeaderSwitcher';

export const ConfigureTaskDrawerHeader = () => {
  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);

  const findActiveTask = useFindActiveTask();
  const { deleteTask, isDeletingTask } = useDeleteTask();
  const { deleteStep, isDeletingStep } = useDeleteStep();

  const isChatLoading = useAssistantLoadingStore((state) => state.isLoading);

  const hasAnyPendingStatus = useFlowBuilder2PageStore((s) => s.hasAnyPendingStatus(), isEqual);
  const activeTab = useFlowBuilder2PageStore((store) => store.rightDrawerActiveTab);
  const setActiveTaskId = useFlowBuilder2PageStore((s) => s.setActiveTaskId);
  const actionIndex = useFlowBuilder2PageStore((s) => s.taskActionIndex);
  const setActionIndex = useFlowBuilder2PageStore((s) => s.setTaskActionIndex);

  const activeAction = useEditorStore((s) => {
    const config = findActiveTask(s.flow);
    return config?.actions?.[actionIndex];
  }, isEqual);
  const actionSetId = useEditorStore((s) => findActiveTask(s.flow)?.actionSetId ?? '');

  const isSingleStepInFlow = useEditorStore(
    (s) => uniqBy(s.flow?.steps, (step) => step.stepId).length === 1,
  );
  const isSingleTask = useEditorStore((s) => {
    if (s.flow && findActiveTask()) {
      const steps = s.flow.steps.filter((step) => step.stepId === findActiveTask()?.stepId);
      const taskIds = steps.map((step) => step.taskId);
      return uniq(taskIds).length === 1;
    }
    return false;
  });

  const isActionOpened = actionIndex >= 0;

  const closeActionView = useCallback(() => {
    setActionIndex(-1);
  }, [setActionIndex]);

  const onClose = useCallback(() => {
    if (isActionOpened) closeActionView();
    else setActiveTaskId(null);
  }, [isActionOpened, closeActionView, setActiveTaskId]);

  const { deleteAction, isDeletingAction } = useDeleteAction({ onSuccess: closeActionView });

  const disabled = isChatLoading || hasAnyPendingStatus;
  const isChatTab = activeTab === BuilderConfigureEditorTabEnum.AskDiana;

  const taskActionMenuActions: IRightDrawerAction[] = [
    {
      icon: DeleteOutlined,
      hidden: disabled,
      onClick: () => {
        if (activeAction && actionSetId) {
          deleteAction(actionSetId, activeAction.id).catch();
        }
      },
      isLoading: isDeletingAction,
      tooltip: t('tooltips.deleteAction'),
    },
  ];

  const taskMenuActions: IRightDrawerAction[] = [
    {
      icon: DeleteOutlined,
      hidden: (isSingleStepInFlow && isSingleTask) || disabled,
      onClick: () => {
        const flowStep = findActiveTask();
        if (flowStep) {
          if (isSingleTask) {
            deleteStep({ stepId: flowStep.stepId }).catch();
          } else {
            deleteTask({ taskId: flowStep.taskId }).catch();
          }
        }
      },
      isLoading: isDeletingTask || isDeletingStep,
      tooltip: t('tooltips.deleteTask'),
    },
  ];

  return (
    <ConfigureDrawerHeader isChat={isChatTab}>
      {isActionOpened ? (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={0.5}
          minHeight={36}
        >
          <Stack direction="row" alignItems="center" gap={0.5}>
            <IconButton onClick={onClose} sx={{ p: 0.75 }}>
              <ArrowBack sx={{ width: 20, height: 20 }} />
            </IconButton>
            <Typography>
              {t('titles.editActionName', {
                actionName: activeAction?.displayName ?? t('labels.end'),
              })}
            </Typography>
          </Stack>
          <RightDrawerActions actions={taskActionMenuActions} />
        </Stack>
      ) : (
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={0.5}>
          <RightDrawerHeaderSwitcher />
          {!isChatTab ? <RightDrawerActions actions={taskMenuActions} /> : null}
        </Stack>
      )}
    </ConfigureDrawerHeader>
  );
};
